/**
 * The table in `docs/section-actions.md` (M3.6a), as data (M3.6b).
 *
 * This file is the only place that says which verb belongs to which section,
 * and it is transcribed, not designed: a test walks the doc's four rows
 * against `SECTION_ACTIONS`. Every action is one of four kinds and nothing
 * else — a Gmail deep link that changes no state, a user observation about
 * openness, a repair, or a suppression — so there is no verb here that could
 * grow a send path (Constitution, *Authority and action*).
 *
 * `Clarify` is absent on purpose: the spec's M3.6a review removed it (▲2), on
 * the grounds that it opened the same thread as Reply and taught nothing.
 */
import type { HistoryClassification, LedgerSectionId, OpennessState, Surface } from '@cordelia/core'
import type { UserSuppressionReason } from '@cordelia/pipeline'

export type ActionId =
  | 'act'
  | 'delegate'
  | 'mark_done'
  | 'correct'
  | 'follow_up'
  | 'mark_received'
  | 'dismiss'
  | 'reply'
  | 'done'
  | 'still_open'
  | 'delegated'
  | 'dropped'

/** The four kinds a product with no send and no team features can offer. */
export type ActionKind = 'deep_link' | 'observation' | 'repair' | 'suppression'

export type ActionSpec = {
  readonly id: ActionId
  readonly section: LedgerSectionId
  readonly label: string
  readonly kind: ActionKind
  /** Behind the overflow menu: the repairs and the reversible discards. */
  readonly overflow: boolean
  /** A reversible discard: last in the menu, after a separator. */
  readonly destructive?: true
  /** An observation appends `user_observation` with this openness. */
  readonly observation?: OpennessState | 'cancelled'
  /** A Probably done? answer, recorded as a HistoryConfirmation and never as a ProductAction. */
  readonly confirmation?: HistoryClassification
  /** A suppression appends `suppression` with this reason. */
  readonly suppression?: UserSuppressionReason
  /** A repair that asks for a party before it can be appended. */
  readonly asksForParty?: true
  /** A repair that hands off to the correction sheet (M4.1) rather than appending here. */
  readonly opensCorrectionSheet?: true
}

/**
 * The table, in the doc's order. `overflow` groups the verbs that say
 * "something is wrong, hand it off, or hide it" behind one control, so the
 * row keeps the section's point (Spec: "the smallest useful actions").
 */
export const SECTION_ACTIONS: readonly ActionSpec[] = [
  { id: 'act', section: 'you-owe', label: 'Act', kind: 'deep_link', overflow: false },
  {
    id: 'mark_done',
    section: 'you-owe',
    label: 'Mark done',
    kind: 'observation',
    overflow: false,
    observation: 'resolved',
  },
  {
    id: 'delegate',
    section: 'you-owe',
    label: 'Delegate',
    kind: 'repair',
    overflow: true,
    asksForParty: true,
  },
  {
    id: 'correct',
    section: 'you-owe',
    label: 'Correct',
    kind: 'repair',
    overflow: true,
    opensCorrectionSheet: true,
  },

  {
    id: 'follow_up',
    section: 'youre-waiting-on',
    label: 'Follow up',
    kind: 'deep_link',
    overflow: false,
  },
  {
    id: 'mark_received',
    section: 'youre-waiting-on',
    label: 'Mark received',
    kind: 'observation',
    overflow: false,
    observation: 'resolved',
  },
  {
    id: 'correct',
    section: 'youre-waiting-on',
    label: 'Correct',
    kind: 'repair',
    overflow: true,
    opensCorrectionSheet: true,
  },
  {
    id: 'dismiss',
    section: 'youre-waiting-on',
    label: 'Dismiss',
    kind: 'suppression',
    overflow: true,
    destructive: true,
    suppression: 'user:dismiss',
  },
  {
    id: 'dismiss',
    section: 'youre-waiting-on',
    label: 'Irrelevant or private',
    kind: 'suppression',
    overflow: true,
    destructive: true,
    suppression: 'user:irrelevant_or_private',
  },

  { id: 'reply', section: 'may-be-expected', label: 'Reply', kind: 'deep_link', overflow: false },
  {
    id: 'correct',
    section: 'may-be-expected',
    label: 'Correct',
    kind: 'repair',
    overflow: true,
    opensCorrectionSheet: true,
  },
  {
    id: 'dismiss',
    section: 'may-be-expected',
    label: 'Dismiss',
    kind: 'suppression',
    overflow: true,
    destructive: true,
    suppression: 'user:dismiss',
  },
  {
    id: 'dismiss',
    section: 'may-be-expected',
    label: 'Irrelevant or private',
    kind: 'suppression',
    overflow: true,
    destructive: true,
    suppression: 'user:irrelevant_or_private',
  },

  {
    id: 'done',
    section: 'probably-done',
    label: 'Done',
    kind: 'observation',
    overflow: false,
    observation: 'resolved',
    confirmation: 'done',
  },
  {
    id: 'still_open',
    section: 'probably-done',
    label: 'Still open',
    kind: 'observation',
    overflow: false,
    observation: 'open',
    confirmation: 'still_open',
  },
  {
    id: 'delegated',
    section: 'probably-done',
    label: 'Delegated',
    kind: 'repair',
    overflow: true,
    confirmation: 'delegated',
    asksForParty: true,
  },
  {
    id: 'dropped',
    section: 'probably-done',
    label: 'Dropped',
    kind: 'observation',
    overflow: true,
    destructive: true,
    observation: 'cancelled',
    confirmation: 'dropped',
  },
]

export const actionsFor = (section: LedgerSectionId): readonly ActionSpec[] =>
  SECTION_ACTIONS.filter((a) => a.section === section)

/** Which section a card is on. Off the four surfaces there are no actions. */
export function sectionOf(surface: Surface): LedgerSectionId | null {
  switch (surface) {
    case 'you-owe':
      return 'you-owe'
    case 'waiting-on':
      return 'youre-waiting-on'
    case 'may-be-expected':
      return 'may-be-expected'
    case 'history':
      return 'probably-done'
    case 'suppressed':
      return null
  }
}

/** A deep link opens a thread and changes nothing; everything else appends. */
export const changesState = (spec: ActionSpec): boolean => spec.kind !== 'deep_link'
